import axios from "./axios";

const UNAVAILABLE_STATUSES = new Set([404, 501]);

function isUnavailableStatus(status) {
  return UNAVAILABLE_STATUSES.has(Number(status));
}

export function isPayrollBasisUnavailableStatus(status) {
  return isUnavailableStatus(status);
}

function unwrapData(payload) {
  if (payload == null) return null;
  if (Object.prototype.hasOwnProperty.call(payload, "data")) {
    return payload.data;
  }
  return payload;
}

/** Collect human-readable messages from a Rails-style error payload. */
export function extractPayrollBasisErrors(payload) {
  if (!payload) return [];
  const errors = payload.errors;
  if (Array.isArray(errors)) {
    return errors.map(String).map((s) => s.trim()).filter(Boolean);
  }
  if (typeof errors === "string" && errors.trim()) {
    return [errors.trim()];
  }
  if (typeof payload.message === "string" && payload.message.trim()) {
    return [payload.message.trim()];
  }
  if (typeof payload.error === "string" && payload.error.trim()) {
    return [payload.error.trim()];
  }
  return [];
}

/**
 * Resolve a display message from an axios error, API result, or raw payload.
 * Prefers `errors[]`, then `message` / `error`.
 */
export function getPayrollBasisErrorMessage(errorOrResult, fallback = "") {
  if (errorOrResult == null) return fallback;

  if (Array.isArray(errorOrResult.errors) && errorOrResult.errors.length) {
    const joined = errorOrResult.errors.map(String).map((s) => s.trim()).filter(Boolean);
    if (joined.length) return joined.join(" ");
  }

  const payload =
    errorOrResult?.response?.data ??
    errorOrResult?.raw ??
    (errorOrResult?.data && typeof errorOrResult.data === "object" && !Array.isArray(errorOrResult.data)
      ? errorOrResult.data
      : null) ??
    (typeof errorOrResult === "object" && !errorOrResult.response ? errorOrResult : null);

  const fromPayload = extractPayrollBasisErrors(payload);
  if (fromPayload.length) return fromPayload.join(" ");

  return fallback;
}

/**
 * Payroll basis API client.
 * Graceful when `/payroll_bases` is temporarily missing (404/501).
 *
 * Contract (API PR-C):
 *   GET    /payroll_bases?year=&month=
 *   POST   /payroll_bases              { year, month, notes? }  — notes = HTML
 *   GET    /payroll_bases/:id
 *   PATCH  /payroll_bases/:id          { notes }                — optional; FE falls back to POST
 *   PATCH  /payroll_bases/:id/employees/:user_id  { manual_note }  — HTML string
 *   POST   /payroll_bases/:id/finalize
 *   POST   /payroll_bases/:id/reopen
 *   POST   /payroll_bases/:id/export
 *   GET    /payroll_bases/:id/export/:job_id
 */

export async function fetchPayrollBases({ year, month } = {}) {
  try {
    const res = await axios.get(`/payroll_bases`, {
      params: {
        ...(year != null ? { year } : {}),
        ...(month != null ? { month } : {}),
      },
      validateStatus: (status) =>
        status === 200 || isUnavailableStatus(status) || status === 403,
    });

    if (isUnavailableStatus(res.status)) {
      return { unavailable: true, data: [], status: res.status };
    }
    if (res.status === 403) {
      const err = new Error("forbidden");
      err.response = res;
      throw err;
    }

    const data = unwrapData(res.data);
    return {
      unavailable: false,
      data: Array.isArray(data) ? data : data ? [data] : [],
      status: res.status,
      raw: res.data,
    };
  } catch (error) {
    const status = error?.response?.status;
    if (isUnavailableStatus(status)) {
      return { unavailable: true, data: [], status };
    }
    throw error;
  }
}

/** Create or refresh draft for year+month. */
export async function createOrRefreshPayrollBasis({ year, month, notes } = {}) {
  try {
    const body = { year, month };
    if (notes != null) body.notes = notes;

    const res = await axios.post(`/payroll_bases`, body, {
      validateStatus: (status) =>
        status === 200 ||
        status === 201 ||
        status === 422 ||
        isUnavailableStatus(status) ||
        status === 403,
    });

    if (isUnavailableStatus(res.status)) {
      return { unavailable: true, data: null, status: res.status };
    }

    if (res.status === 403) {
      const err = new Error("forbidden");
      err.response = res;
      throw err;
    }

    return {
      unavailable: false,
      data: unwrapData(res.data),
      status: res.status,
      errors: extractPayrollBasisErrors(res.data),
      raw: res.data,
    };
  } catch (error) {
    const status = error?.response?.status;
    if (isUnavailableStatus(status)) {
      return { unavailable: true, data: null, status };
    }
    throw error;
  }
}

export async function fetchPayrollBasis(id) {
  try {
    const res = await axios.get(`/payroll_bases/${id}`, {
      validateStatus: (status) =>
        status === 200 ||
        status === 404 ||
        status === 501 ||
        status === 403,
    });

    // 501 = endpoint missing. 404 on show = record not found (still openable via index summary).
    if (res.status === 501) {
      return { unavailable: true, data: null, status: res.status, notFound: false };
    }
    if (res.status === 404) {
      return {
        unavailable: false,
        data: null,
        status: 404,
        notFound: true,
        errors: extractPayrollBasisErrors(res.data),
        raw: res.data,
      };
    }
    if (res.status === 403) {
      const err = new Error("forbidden");
      err.response = res;
      throw err;
    }

    return {
      unavailable: false,
      data: unwrapData(res.data),
      status: res.status,
      notFound: false,
      raw: res.data,
    };
  } catch (error) {
    const status = error?.response?.status;
    if (status === 501) {
      return { unavailable: true, data: null, status, notFound: false };
    }
    if (status === 404) {
      return {
        unavailable: false,
        data: null,
        status: 404,
        notFound: true,
        errors: extractPayrollBasisErrors(error?.response?.data),
        raw: error?.response?.data,
      };
    }
    throw error;
  }
}

/**
 * Update general period notes (HTML string).
 * Prefers PATCH /payroll_bases/:id { notes }; falls back to POST create/refresh
 * with notes when PATCH is not available yet (API may land in parallel).
 */
export async function updatePayrollBasisNotes({
  payrollBasisId,
  notes,
  year,
  month,
}) {
  const body = { notes };

  if (payrollBasisId != null) {
    try {
      const res = await axios.patch(`/payroll_bases/${payrollBasisId}`, body, {
        validateStatus: (status) =>
          status === 200 ||
          status === 204 ||
          status === 404 ||
          status === 405 ||
          status === 422 ||
          status === 403 ||
          isUnavailableStatus(status),
      });

      if (res.status === 200 || res.status === 204) {
        return {
          unavailable: false,
          data: unwrapData(res.data) ?? { notes },
          status: res.status,
          raw: res.data,
        };
      }

      if (res.status === 422 || res.status === 403) {
        const err = new Error("update_notes_failed");
        err.response = res;
        throw err;
      }
      // 404/405/501 → fall through to POST create/refresh with notes
    } catch (error) {
      if (error?.message === "update_notes_failed") throw error;
      const status = error?.response?.status;
      if (status === 422 || status === 403) throw error;
      if (
        status != null &&
        !isUnavailableStatus(status) &&
        status !== 404 &&
        status !== 405
      ) {
        throw error;
      }
    }
  }

  if (year == null || month == null) {
    const err = new Error("update_notes_missing_period");
    throw err;
  }

  return createOrRefreshPayrollBasis({ year, month, notes });
}

export async function updatePayrollBasisEmployeeNote({
  payrollBasisId,
  userId,
  manualNote,
}) {
  try {
    // Prefer manual_note; also send manual_note_html alias (API accepts both).
    const res = await axios.patch(
      `/payroll_bases/${payrollBasisId}/employees/${userId}`,
      { manual_note: manualNote, manual_note_html: manualNote },
      {
        validateStatus: (status) =>
          status === 200 ||
          status === 422 ||
          status === 403 ||
          isUnavailableStatus(status),
      }
    );

    if (isUnavailableStatus(res.status)) {
      return { unavailable: true, data: null, status: res.status };
    }

    if (res.status >= 400) {
      const err = new Error("update_employee_failed");
      err.response = res;
      throw err;
    }

    return {
      unavailable: false,
      data: unwrapData(res.data),
      status: res.status,
      raw: res.data,
    };
  } catch (error) {
    const status = error?.response?.status;
    if (isUnavailableStatus(status)) {
      return { unavailable: true, data: null, status };
    }
    throw error;
  }
}

export async function finalizePayrollBasis(id) {
  try {
    const res = await axios.post(
      `/payroll_bases/${id}/finalize`,
      {},
      {
        validateStatus: (status) =>
          status === 200 ||
          status === 422 ||
          status === 403 ||
          isUnavailableStatus(status),
      }
    );

    if (isUnavailableStatus(res.status)) {
      return { unavailable: true, data: null, status: res.status };
    }

    if (res.status >= 400) {
      const err = new Error("finalize_failed");
      err.response = res;
      throw err;
    }

    return {
      unavailable: false,
      data: unwrapData(res.data),
      status: res.status,
      raw: res.data,
    };
  } catch (error) {
    const status = error?.response?.status;
    if (isUnavailableStatus(status)) {
      return { unavailable: true, data: null, status };
    }
    throw error;
  }
}

/** Reopen finalized/exported → draft so notes/refresh are editable again. */
export async function reopenPayrollBasis(id) {
  try {
    const res = await axios.post(
      `/payroll_bases/${id}/reopen`,
      {},
      {
        validateStatus: (status) =>
          status === 200 ||
          status === 422 ||
          status === 403 ||
          isUnavailableStatus(status),
      }
    );

    if (isUnavailableStatus(res.status)) {
      return { unavailable: true, data: null, status: res.status };
    }

    if (res.status >= 400) {
      const err = new Error("reopen_failed");
      err.response = res;
      throw err;
    }

    return {
      unavailable: false,
      data: unwrapData(res.data),
      status: res.status,
      raw: res.data,
    };
  } catch (error) {
    const status = error?.response?.status;
    if (isUnavailableStatus(status)) {
      return { unavailable: true, data: null, status };
    }
    throw error;
  }
}

/**
 * Enqueue ZIP export. Returns { job_id, status, export_kind }.
 * For draft: pass mode "finalize_and_export" | "export_draft" (required by API).
 * For finalized/exported: omit mode (re-download).
 */
export async function startPayrollBasisExport(id, { mode } = {}) {
  try {
    const body = {};
    if (mode) body.mode = mode;

    const res = await axios.post(
      `/payroll_bases/${id}/export`,
      body,
      {
        validateStatus: (status) =>
          status === 200 ||
          status === 202 ||
          status === 422 ||
          isUnavailableStatus(status) ||
          status === 403,
      }
    );

    if (isUnavailableStatus(res.status)) {
      return { unavailable: true, data: null, status: res.status };
    }

    if (res.status === 422) {
      return {
        unavailable: false,
        data: unwrapData(res.data),
        status: res.status,
        errors: extractPayrollBasisErrors(res.data),
        raw: res.data,
      };
    }

    if (res.status >= 400) {
      const err = new Error("export_failed");
      err.response = res;
      throw err;
    }

    return {
      unavailable: false,
      data: unwrapData(res.data),
      status: res.status,
      raw: res.data,
    };
  } catch (error) {
    const status = error?.response?.status;
    if (isUnavailableStatus(status)) {
      return { unavailable: true, data: null, status };
    }
    throw error;
  }
}

/** Poll export job status. Completed metadata typically includes zip_url + filename. */
export async function fetchPayrollBasisExportStatus(payrollBasisId, jobId) {
  try {
    const res = await axios.get(
      `/payroll_bases/${payrollBasisId}/export/${jobId}`,
      {
        validateStatus: (status) =>
          status === 200 ||
          status === 404 ||
          isUnavailableStatus(status) ||
          status === 403,
      }
    );

    if (isUnavailableStatus(res.status)) {
      return { unavailable: true, data: null, status: res.status };
    }

    if (res.status === 403) {
      const err = new Error("forbidden");
      err.response = res;
      throw err;
    }

    return {
      unavailable: false,
      data: unwrapData(res.data),
      status: res.status,
      raw: res.data,
    };
  } catch (error) {
    const status = error?.response?.status;
    if (isUnavailableStatus(status)) {
      return { unavailable: true, data: null, status };
    }
    throw error;
  }
}

export function triggerBrowserDownload(url, filename) {
  if (!url) return;
  const link = document.createElement("a");
  link.href = url;
  if (filename) link.download = filename;
  link.rel = "noopener noreferrer";
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
